import { UsersDataTable } from "components/UsersDataTable";
import AuthGuard from "context/auth/AuthGuard";
import { UserRoles } from "constants/userRoles";
import { Box, CircularProgress, Divider, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useGetSuperiors } from "hooks/useGetSuperiors";
import { useGetSubordinates } from "hooks/useGetSubordinates";
import { useAssignUsersToPatron } from "hooks/useAssignUsersToPatron";
import { UserWorkspaceProfile } from "models/UserWorkspaceProfile";
import React from "react";
import ConnectButton from "components/ConnectButton/ConnectButton";
import { MainStack, TitleTypography, SubtitleTypography } from './UsersManagementPage.styles';
import { UserProfileViewContextProvider } from "context/userProfile/UserProfileViewContextProvider";

export const UsersManagementPage = React.memo(() => {
    const [windowState, setWindowState] = useState<'initial' | 'fetchingData' | 'dataFetched' | 'error'>('initial');
    const [superiorsSelected, setSuperiorsSelected] = useState<number[]>([]);
    const [subordinatesSelected, setSubordinatesSelected] = useState<number[]>([]);
    const [superiorsData, setSuperiorsData] = useState<UserWorkspaceProfile[]>([]);
    const [subordinatesData, setSubordinatesData] = useState<UserWorkspaceProfile[]>([]);
    const [expandedRowsSuperiors, setExpandedRowsSuperiors] = useState<string[]>([]);
    const [expandedRowsSubordinates, setExpandedRowsSubordinates] = useState<string[]>([]);
    const { fetchSuperiors, loading: fetchingSuperiors} = useGetSuperiors();
    const { fetchSubordinates, loading: fetchingSubordinates } = useGetSubordinates();
    const { assignUsersToPatron, assigningUsersToPatron } = useAssignUsersToPatron();
    const { t } = useTranslation();
    const theme = useTheme();

    const handleAssignUsersToPatron = async () => {
        assignUsersToPatron(superiorsSelected.map(s => superiorsData[s].id), subordinatesSelected.map(s => subordinatesData[s].id));
    }    

    const updateUsers = (userId: string, newRole: UserRoles) => {
        const patron = superiorsData.find(s => s.id === userId);
        const participant = subordinatesData.find(s => s.id === userId);
        
        if (patron) {
            patron.role = newRole;

            if (newRole === UserRoles.Participant) {
                setSuperiorsData(superiorsData.filter(s => s.id !== userId));
                setSubordinatesData([...subordinatesData, patron]);
            } else {
                setSuperiorsData(superiorsData.map(s => {
                    if (s.id === userId) {
                        return {...s, role: newRole };
                    }
                    return s;
                }));
            }
        }
        
        if (participant) {
            participant.role = newRole;

            if (newRole === UserRoles.Patron || newRole === UserRoles.PatronLight) {
                setSubordinatesData(subordinatesData.filter(s => s.id !== userId));
                setSuperiorsData([...superiorsData, participant]);
            } else {
                setSubordinatesData(subordinatesData.map(s => {
                    if (s.id === userId) {
                        return {...s, role: newRole };
                    }
                    return s;
                }));
            }
        }

        setExpandedRowsSuperiors([]);
        setExpandedRowsSubordinates([]);
    };

    useEffect(() => {
        if (windowState !== 'initial') {
            setWindowState('initial');
        }
    }, [fetchSubordinates, fetchSuperiors]);


    useEffect(() => {
        const fetchData = async () => {
            setWindowState('fetchingData');
            fetchSuperiors().then(resp => {
                if (resp?.success && resp.data) {
                    setSuperiorsData(resp.data);
                } else {
                    setSuperiorsData([]);
                }

                fetchSubordinates().then(resp => {
                    if (resp?.success && resp.data) {
                        setSubordinatesData(resp.data);
                    } else {
                        setSubordinatesData([]);
                    }

                    setWindowState('dataFetched');
                });
            });
                
        };

        if (windowState === 'initial') {
            fetchData();
        }
    }, [windowState]);

    return <AuthGuard requiredRoles={[UserRoles.ADMIN, UserRoles.Patron, UserRoles.PatronLight]}>            
            {windowState === 'fetchingData'
            ? <CircularProgress />
            :
            <UserProfileViewContextProvider>
                <MainStack>
                    <TitleTypography variant="h4">
                        {t('usersManagement.title')}
                    </TitleTypography>
                    <SubtitleTypography variant="subtitle1">
                        {t('usersManagement.subtitle')}
                    </SubtitleTypography>
                    <Divider />
                    <Stack direction={'row'} width={'100%'}>
                        <Box width={'100%'} paddingRight={theme.spacing(5)} paddingTop={theme.spacing(5)}>
                            <UsersDataTable
                                title={t('usersManagement.patrons')}
                                users={superiorsData}
                                fetchingUsers={fetchingSubordinates}
                                setParentUsersSelected={setSuperiorsSelected}
                                onUserRoleChanged={updateUsers}
                                isPatronsTable={true}
                                expandedRows={expandedRowsSuperiors}
                                setExpandedRows={setExpandedRowsSuperiors}
                            />
                        </Box>
                        <ConnectButton
                            onClick={handleAssignUsersToPatron}
                            disabled={!(superiorsSelected.length > 0 && subordinatesSelected.length > 0)}
                            loading={assigningUsersToPatron}
                        />
                        <Box width={'100%'} paddingLeft={theme.spacing(5)} paddingTop={theme.spacing(5)}>
                            <UsersDataTable
                                title={t('usersManagement.participants')}
                                users={subordinatesData}
                                fetchingUsers={fetchingSuperiors}
                                setParentUsersSelected={setSubordinatesSelected}
                                onUserRoleChanged={updateUsers}
                                expandedRows={expandedRowsSubordinates}
                                setExpandedRows={setExpandedRowsSubordinates}
                            />
                        </Box>
                    </Stack>
                    
                </MainStack>
            </UserProfileViewContextProvider>}
        </AuthGuard>
});